import {
  ButtonProps,
  ButtonLinkProps,
  BreadcrumbsLinkProps,
  ChipProps,
  Tooltip,
  ButtonLink,
  Button,
  Flex,
  Menu,
  Breadcrumbs,
  Chip,
  Text,
  TextField,
} from "../";
import {
  ComponentPropsWithoutRef,
  ReactNode,
  forwardRef,
  Ref,
  RefObject,
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
} from "react";
import Layout, { LayoutProps } from "../Layout";
import cx from "classnames";
import styles from "./Page.module.scss";
import LocalSettings from "@servicetitan/hammer-icon/mdi/round/settings.svg";
import MoreVert from "@servicetitan/hammer-icon/mdi/round/more_vert.svg";
import Edit from "@servicetitan/hammer-icon/mdi/round/edit.svg";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";

/**
 * Props for page header action buttons
 */
type PageHeaderActionProps = Omit<ButtonProps, "appearance"> & {
  /**
   * Label text for the action button
   */
  label: string;
};

/**
 * Props for page header actions configuration
 */
type PageHeaderActionsProps = {
  /**
   * Primary action button
   */
  primary?: PageHeaderActionProps;
  /**
   * Array of secondary action buttons
   */
  secondary?: PageHeaderActionProps[];
};

/**
 * Base props for preference actions
 */
type BasePreferenceProps = {
  /**
   * Accessible label for the preference action
   */
  "aria-label": string;
};

/**
 * Props for preference button actions
 */
type PreferenceButtonProps = Omit<
  ButtonProps,
  "aria-label" | "icon" | "appearance" | "size" | "className"
> &
  BasePreferenceProps;

/**
 * Props for preference link actions
 */
type PreferenceLinkProps = Omit<
  ButtonLinkProps,
  "aria-label" | "icon" | "appearance" | "size" | "className"
> &
  BasePreferenceProps;

type PageHeaderTitleConfig = {
  text: string;
  onChange?: (title: string) => void | boolean;
  editButtonLabel?: string;
  editFieldLabel?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
};

type PageHeaderTitle = PageHeaderTitleConfig["text"] | PageHeaderTitleConfig;

/**
 * Props for the PageHeader component
 * @extends ComponentPropsWithoutRef<"header">
 */
export type PageHeaderProps = Omit<
  ComponentPropsWithoutRef<"header">,
  "title"
> & {
  /**
   * Main title of the page
   */
  title: PageHeaderTitle;
  /**
   * Breadcrumb navigation links
   */
  breadcrumbs?: BreadcrumbsLinkProps[];
  /**
   * Status chips to display
   */
  chips?: ChipProps[];
  /**
   * Description text or content below the title
   */
  description?: ReactNode;
  /**
   * Primary and secondary action buttons
   */
  actions?: PageHeaderActionsProps;
  /**
   * Preference action button or link
   */
  preferenceAction?: PreferenceButtonProps | PreferenceLinkProps;
  /**
   * Layout configuration for the header
   */
  layout?: LayoutProps;
};

/**
 * PageHeader component for creating page headers with navigation and actions.
 *
 * Features:
 * - Page title with semantic heading
 * - Breadcrumb navigation support
 * - Status chips display
 * - Primary and secondary action buttons
 * - Preference action button/link
 * - Responsive layout with fluid grid
 * - Accessibility support with proper ARIA labels
 * - Flexible layout configuration
 * - Description content support
 *
 * @example
 * <Page.Header
 *   title="Page Title"
 *   breadcrumbs={[
 *     { href: "/", children: "Home" },
 *     { children: "Current Page" }
 *   ]}
 *   chips={[{ label: "Status" }]}
 *   actions={{
 *     primary: { label: "Save", onClick: handleSave },
 *     secondary: [{ label: "Cancel", onClick: handleCancel }]
 *   }}
 * />
 */
export const PageHeader = forwardRef(
  (props: PageHeaderProps, ref: Ref<HTMLHeadElement>) => {
    const {
      title: titleProp,
      breadcrumbs,
      chips,
      description,
      actions,
      preferenceAction,
      layout,
      className,
      ...rest
    } = props;

    const title: PageHeaderTitleConfig =
      typeof titleProp === "string" ? { text: titleProp } : titleProp;

    const [editing, setEditing] = useState(false);
    const [editingTitle, setEditingTitle] = useState<
      PageHeaderTitleConfig["text"]
    >(title.text);
    const editFieldRef = useRef<HTMLInputElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);
    const firstRenderRef = useRef(true);

    useEffect(() => {
      if (firstRenderRef.current) {
        firstRenderRef.current = false;
        return;
      }

      if (editing === true && editFieldRef.current) {
        editFieldRef.current.focus();
      }

      if (editing === false && editButtonRef.current) {
        editButtonRef.current.focus();
      }
    }, [editing]);

    const { fluid, variant } = layout || {};

    const preferenceItem =
      preferenceAction &&
      ("href" in preferenceAction ? (
        <Tooltip>
          <Tooltip.Trigger>
            <ButtonLink
              appearance="ghost"
              icon={LocalSettings}
              size="small"
              className={styles["preference-action"]}
              {...(preferenceAction as PreferenceLinkProps)}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{preferenceAction["aria-label"]}</Tooltip.Content>
        </Tooltip>
      ) : (
        <Tooltip>
          <Tooltip.Trigger>
            <Button
              appearance="ghost"
              icon={LocalSettings}
              size="small"
              className={styles["preference-action"]}
              {...(preferenceAction as PreferenceButtonProps)}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{preferenceAction["aria-label"]}</Tooltip.Content>
        </Tooltip>
      ));

    const actionItems = actions && (
      <Flex justifyContent="flex-end" className={styles.actions}>
        {/* Only primary action */}
        {actions.primary &&
          (!actions.secondary || actions.secondary.length === 0) && (
            <Button appearance="primary" {...actions.primary}>
              {actions.primary.label}
            </Button>
          )}

        {/* Only secondary actions (1-2 buttons) */}
        {!!actions.secondary?.length &&
          actions.secondary.length < 3 &&
          !actions.primary &&
          actions.secondary.map((action, index) => (
            <Button key={index} appearance="secondary" {...action}>
              {action.label}
            </Button>
          ))}

        {/* 1 secondary action + primary action */}
        {actions.primary &&
          !!actions.secondary?.length &&
          actions.secondary.length === 1 && (
            <>
              <Button appearance="secondary" {...actions.secondary[0]}>
                {actions.secondary[0].label}
              </Button>
              <Button appearance="primary" {...actions.primary}>
                {actions.primary.label}
              </Button>
            </>
          )}

        {/* Multiple secondary actions (3+) with optional primary */}
        {!!actions.secondary?.length &&
          (actions.secondary.length >= 3 ||
            (actions.secondary.length >= 2 && actions.primary)) && (
            <>
              <Menu
                trigger={({ ref, ...rest }) => (
                  <Tooltip>
                    <Tooltip.Trigger
                      ref={ref as unknown as RefObject<HTMLDivElement>}
                    >
                      <Button
                        appearance="secondary"
                        icon={MoreVert}
                        aria-label="More actions"
                        {...rest}
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Content>More actions</Tooltip.Content>
                  </Tooltip>
                )}
              >
                {actions.secondary.map((action, index) => (
                  <Menu.Item
                    key={index}
                    label={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  />
                ))}
              </Menu>
              {actions.primary && (
                <Button appearance="primary" {...actions.primary}>
                  {actions.primary.label}
                </Button>
              )}
            </>
          )}
      </Flex>
    );

    const headerClassName = cx(styles.content, styles.header, className);

    const headerContentClassName = cx(
      styles["content-inner"],
      styles["content-header-inner"],
    );

    return (
      <header ref={ref} className={headerClassName} {...rest}>
        <div className={headerContentClassName}>
          <Layout fluid={fluid} variant={variant} className={styles["layout"]}>
            {breadcrumbs && (
              <Layout.Item>
                <Breadcrumbs>
                  {breadcrumbs.map((breadcrumb, index) => (
                    <Breadcrumbs.Link key={index} {...breadcrumb} />
                  ))}
                </Breadcrumbs>
              </Layout.Item>
            )}

            <Layout.Item md={actionItems || editing ? 6 : 12}>
              <div className={styles["title-wrapper"]}>
                {title.onChange && editing ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (title.onChange?.(editingTitle) !== false) {
                        setEditing(false);
                      }
                    }}
                    style={{ display: "flex" }}
                  >
                    <Flex alignItems="center" grow={1} gap={2}>
                      <TextField
                        aria-label={title.editFieldLabel ?? "Page title"}
                        defaultValue={title.text}
                        flexGrow={1}
                        ref={editFieldRef}
                        onChange={(event) =>
                          setEditingTitle(event.target.value)
                        }
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                          if (e.code === "Escape") {
                            setEditing(false);
                            setEditingTitle(title.text);
                          }
                        }}
                      />

                      <Flex alignItems="center" gap={1}>
                        <Tooltip>
                          <Tooltip.Trigger>
                            <Button
                              icon={Check}
                              aria-label={
                                title.confirmButtonLabel ?? "Update title"
                              }
                              size="small"
                              appearance="primary"
                              onClick={() => {
                                if (title.onChange?.(editingTitle) !== false) {
                                  setEditing(false);
                                }
                              }}
                            />
                          </Tooltip.Trigger>
                          <Tooltip.Content>
                            {title.confirmButtonLabel ?? "Update title"}
                          </Tooltip.Content>
                        </Tooltip>

                        <Tooltip>
                          <Tooltip.Trigger>
                            <Button
                              icon={Close}
                              aria-label={title.cancelButtonLabel ?? "Cancel"}
                              size="small"
                              onClick={() => {
                                setEditing(false);
                                setEditingTitle(title.text);
                              }}
                            />
                          </Tooltip.Trigger>
                          <Tooltip.Content>
                            {title.cancelButtonLabel ?? "Cancel"}
                          </Tooltip.Content>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </form>
                ) : (
                  <>
                    <Text
                      variant="headline"
                      size="large"
                      el="h1"
                      className={styles.title}
                    >
                      {title.text}
                    </Text>

                    {title.onChange != null ? (
                      <>
                        {" "}
                        <Tooltip>
                          <Tooltip.Trigger>
                            <Button
                              icon={Edit}
                              aria-label={title.editButtonLabel ?? "Edit title"}
                              size="small"
                              appearance="ghost"
                              ref={editButtonRef}
                              onClick={() => setEditing(true)}
                            />
                          </Tooltip.Trigger>
                          <Tooltip.Content>
                            {title.editButtonLabel ?? "Edit title"}
                          </Tooltip.Content>
                        </Tooltip>
                      </>
                    ) : null}
                  </>
                )}

                {preferenceItem}
              </div>

              {chips && (
                <Flex className={styles.chips}>
                  {chips.map((chip, index) => (
                    <Chip key={index} {...chip} />
                  ))}
                </Flex>
              )}
            </Layout.Item>

            {actions && <Layout.Item md={6}>{actionItems}</Layout.Item>}

            {description && (
              <Layout.Item md={12} className={styles.description}>
                {description}
              </Layout.Item>
            )}
          </Layout>
        </div>
      </header>
    );
  },
);

PageHeader.displayName = "Page.Header";
